import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faCheck, faTrash, faCircle, faUsers, 
  faShieldAlt, faExclamationCircle, faArrowLeft 
} from '@fortawesome/free-solid-svg-icons';
import { NotificationContext } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

// Styled Components
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 100px 20px 20px 20px; /* Added more top padding for header */
  
  @media (max-width: 768px) {
    padding: 90px 15px 15px 15px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-light);
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 15px;
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s;
  
  &:hover {
    color: white;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  svg {
    margin-right: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 6px 10px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 20px;
`;

const Tab = styled.button`
  flex: 1;
  background: none;
  border: none;
  padding: 12px;
  color: ${props => props.$active ? 'var(--primary-light)' : 'var(--text-dim)'};
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
  border-bottom: 3px solid ${props => props.$active ? 'var(--primary-light)' : 'transparent'};
  margin-bottom: -2px;
  
  &:hover {
    color: ${props => props.$active ? 'var(--primary-light)' : 'white'};
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 10px;
    gap: 6px;
  }
`;

const TabBadge = styled.span`
  display: ${props => props.$count > 0 ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  background-color: var(--error-color);
  color: white;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  padding: 0 6px;
  
  @media (max-width: 480px) {
    min-width: 18px;
    height: 18px;
    font-size: 10px;
  }
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotificationCard = styled.div`
  padding: 15px 5px;
  cursor: pointer;
  display: flex;
  position: relative;
  transition: all 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  ${props => !props.$read && `
    background-color: rgba(59, 130, 246, 0.05);
  `}
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
  
  @media (max-width: 480px) {
    padding: 12px 5px;
  }
`;

const UnreadIndicator = styled.div`
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  margin-right: 12px;
  color: #ffa500; /* Orange color for better visibility */
  font-size: 12px;
  
  @media (max-width: 480px) {
    margin-right: 8px;
    font-size: 10px;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
  color: var(--text-light);
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const NotificationMessage = styled.div`
  font-size: 14px;
  color: var(--text-dim);
  margin-bottom: 10px;
  line-height: 1.4;
  white-space: pre-wrap; /* Preserve line breaks in messages */
  
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.3;
  }
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: var(--text-dim);
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  padding: 8px;
  margin-left: 5px;
  opacity: 0.7;
  transition: opacity 0.2s;
  align-self: flex-start;
  
  &:hover {
    color: var(--error-color);
    opacity: 1;
  }
  
  @media (max-width: 480px) {
    padding: 6px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-dim);
  font-size: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 480px) {
    padding: 30px 15px;
    font-size: 14px;
  }
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-light);
  font-size: 16px;
  
  @media (max-width: 480px) {
    padding: 30px 15px;
    font-size: 14px;
  }
`;

const NotificationsPage = () => {
  const { 
    notifications, 
    unreadCount, 
    loading,
    fetchNotifications,
    markAsRead, 
    markAllAsRead, 
    deleteNotification, 
    deleteAllNotifications 
  } = useContext(NotificationContext);
  
  const [activeTab, setActiveTab] = useState('community');
  const navigate = useNavigate();
  
  // Check if a notification is read (handles both read and is_read properties)
  const isNotificationRead = (notification) => {
    return notification.read === true || notification.is_read === true;
  };
  
  // Filtered notifications based on active tab
  const adminTypes = ['admin_message', 'suggestion_approved', 'suggestion_rejected'];
  
  const communityNotifications = notifications.filter(notification => 
    !adminTypes.includes(notification.type));
  
  const systemNotifications = notifications.filter(notification => 
    adminTypes.includes(notification.type));
  
  // Calculate unread counts for each tab
  const communityUnreadCount = communityNotifications.filter(notification => 
    !isNotificationRead(notification)).length;
  
  const systemUnreadCount = systemNotifications.filter(notification => 
    !isNotificationRead(notification)).length;
  
  // Determine which notifications to show based on active tab
  const displayNotifications = activeTab === 'community' 
    ? communityNotifications 
    : systemNotifications;
  
  // Refresh notifications on page load
  useEffect(() => {
    // Call once on component mount
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove fetchNotifications from dependency array to prevent multiple calls
  
  const handleNotificationClick = (e, notification) => {
    // Prevent default behavior and stop propagation
    e.preventDefault();
    e.stopPropagation();
    
    // Mark as read
    if (!isNotificationRead(notification)) {
      markAsRead(notification._id);
    }
    
    // No navigation functionality - notifications only mark as read
  };
  
  const handleMarkAllAsRead = (e) => {
    e.preventDefault();
    e.stopPropagation();
    markAllAsRead();
  };
  
  const handleDeleteAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    deleteAllNotifications();
  };
  
  const handleDeleteNotification = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    deleteNotification(id);
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handleBack = () => {
    navigate(-1);
  };
  
  // Format time to relative format (e.g. "2 hours ago") with IST adjustment (+5:30 hours)
  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Unknown time';
    
    try {
      // Parse the UTC date string
      const date = new Date(dateString);
      
      // Add 5 hours and 30 minutes for IST adjustment
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      
      const now = new Date();
      // Also adjust current time to match the same adjustment
      const adjustedNow = new Date(now.getTime());
      
      const diffInSeconds = Math.floor((adjustedNow - istDate) / 1000);
      
      if (diffInSeconds < 60) {
        return 'just now';
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ago`;
      } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} ${days === 1 ? 'day' : 'days'} ago`;
      } else {
        // Format date with IST time
        return istDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) + ' IST';
      }
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Unknown time';
    }
  };
  
  // Get notification title based on type
  const getNotificationTitle = (notification) => {
    switch (notification.type) {
      case 'admin_message':
        return 'Message from Admin';
      case 'suggestion_approved':
        return 'Suggestion Approved';
      case 'suggestion_rejected':
        return 'Suggestion Rejected';
      case 'comment_reply':
        return 'New Reply';
      default:
        return 'Notification';
    }
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <BackButton onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </BackButton>
        <NotificationActions>
          <ActionButton onClick={handleMarkAllAsRead}>
            <FontAwesomeIcon icon={faCheck} /> Mark all read
          </ActionButton>
          <ActionButton onClick={handleDeleteAll}>
            <FontAwesomeIcon icon={faTrash} /> Clear all
          </ActionButton>
        </NotificationActions>
      </PageHeader>
      
      <TabsContainer>
        <Tab 
          $active={activeTab === 'community'} 
          onClick={() => handleTabChange('community')}
        >
          <FontAwesomeIcon icon={faUsers} /> Community
          {communityUnreadCount > 0 && (
            <TabBadge $count={communityUnreadCount}>
              {communityUnreadCount}
            </TabBadge>
          )}
        </Tab>
        <Tab 
          $active={activeTab === 'system'} 
          onClick={() => handleTabChange('system')}
        >
          <FontAwesomeIcon icon={faShieldAlt} /> System
          {systemUnreadCount > 0 && (
            <TabBadge $count={systemUnreadCount}>
              {systemUnreadCount}
            </TabBadge>
          )}
        </Tab>
      </TabsContainer>
      
      {loading ? (
        <LoadingState>Loading notifications...</LoadingState>
      ) : displayNotifications.length > 0 ? (
        <NotificationList>
          {displayNotifications.map(notification => (
            <NotificationCard 
              key={notification._id} 
              $read={isNotificationRead(notification)}
              onClick={(e) => handleNotificationClick(e, notification)}
            >
              <UnreadIndicator $visible={!isNotificationRead(notification)}>
                <FontAwesomeIcon icon={faCircle} />
              </UnreadIndicator>
              <NotificationContent>
                {notification.title ? (
                  <>
                    <NotificationTitle>
                      {notification.title}
                    </NotificationTitle>
                    <NotificationMessage>
                      {notification.message}
                    </NotificationMessage>
                  </>
                ) : (
                  <>
                    <NotificationTitle>
                      {getNotificationTitle(notification)}
                    </NotificationTitle>
                    <NotificationMessage>
                      {notification.message}
                    </NotificationMessage>
                  </>
                )}
                <NotificationTime>
                  {formatRelativeTime(notification.createdAt || notification.created_at)}
                </NotificationTime>
              </NotificationContent>
              <DeleteButton 
                onClick={(e) => handleDeleteNotification(e, notification._id)}
                aria-label="Delete notification"
              >
                <FontAwesomeIcon icon={faTimes} />
              </DeleteButton>
            </NotificationCard>
          ))}
        </NotificationList>
      ) : (
        <EmptyState>
          <FontAwesomeIcon icon={faExclamationCircle} style={{ fontSize: '24px', marginBottom: '10px' }} />
          <p>No {activeTab} notifications</p>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default NotificationsPage; 